import { Location } from '@angular/common';
import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import { IContact, IRecurrencyData } from 'app/interfaces/contact.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { ChatService } from 'app/services/chat.service';
import { ContactsService } from 'app/services/contacts.service';
import { UserService } from 'app/services/user.service';
import { slideDownAnimation } from 'app/utils/animations.util';
import { DateTime } from 'luxon';

@Component({
	selector: 'contact-page',
	templateUrl: './contact-page.component.html',
	styleUrls: ['./contact-page.component.scss'],
	animations: [slideDownAnimation],
})
export class ContactPage {
	contact: IContact;
	loading = true;
	cleanUrl: string;
	selectedTabIndex = 0;
	professionalId = null;
	isExportModalOpen = false;
	recurrencyData: IRecurrencyData;
	userData;
	promptuariesFilter = null;
	pages = [
		{
			path: '/',
			name: 'CONTACT.PAGES.PROGRESS',
			value: 'progress',
		},
		{
			path: '/appointments',
			name: 'CONTACT.PAGES.PROMPTUARY',
			value: 'appointments',
		},
		{
			path: '/notes',
			name: 'CONTACT.PAGES.NOTES',
			state: 'hidden',
			value: 'notes',
		},
		{
			path: '/content',
			name: 'CONTACT.PAGES.CONTENT.NAME',
			value: 'contents',
		},
		{
			path: '/tests',
			name: 'CONTACT.PAGES.TESTS',
			value: 'tests',
		},
	];

	constructor(
		private activatedRoute: ActivatedRoute,
		private chatService: ChatService,
		private contactsService: ContactsService,
		private location: Location,
		private notificationService: ZkNotificationService,
		public router: Router,
		private translateService: TranslateService,
		private userService: UserService,
		private atService: ActionTrackerService
	) {
		this.activatedRoute.params.subscribe((params) => {
			this.contactsService
				.getContactRecurrencyData(parseInt(params['contactId']))
				.toPromise()
				.then((recurrencyData) => {
					this.recurrencyData = recurrencyData;
				});
			this.contactsService.getContact(parseInt(params['contactId'])).subscribe(
				(result) => {
					this.contact = result;
					this.loading = false;
				},
				() => {
					this.notificationService.showError(
						this.translateService.instant('CONTACT.ERRORS.ID_NOTFOUND')
					);

					this.router.navigate(['..']);
				}
			);

			this.cleanUrl = this.router.url.replace(`/${params['tabPath']}`, '');
			this.activeTab(params['tabPath']);
		});

		const userData = this.userService.getUserLocal();
		this.userData = userData;
		this.professionalId = userData.id;
		this.pages[2].state = userData.specialistIsCoe ? 'visible' : 'hidden';
	}

	activeTab(tabPath: string): void {
		const pageIndex = this.pages.findIndex(
			(page) => page.path === `/${tabPath}`
		);

		this.changeTab(pageIndex > 0 ? pageIndex : 0);
	}

	goTo(event) {
		if (event.tab == 'evaluation') {
			this.changeTab(4, event.item);
		} else if (event.tab == 'contents') {
			this.changeTab(3);
		} else if (event.tab === 'promptuary') {
			this.router.navigate([
				`/promptuary/${event.item}/${this.contact.refUserId}`,
			]);
		} else if (event.tab === 'appointments') {
			this.changeTab(1);
		}
	}

	changeTab(selectedTabIndex: number, itemId?): void {
		this.selectedTabIndex = selectedTabIndex;
		this.location.replaceState(
			`${this.cleanUrl}${this.pages[selectedTabIndex].path}`,
			'',
			{ itemId }
		);

		this.trackTabView(selectedTabIndex);
	}

	trackTabView(index) {
		this.atService.eventTracker(
			SCOPE_TYPE.Contact,
			SUBSCOPE_TYPE.Profile,
			ITEM_TYPE.Tab,
			null,
			{ tab: this.pages[index].value }
		);
	}

	editContact(): void {
		this.router.navigate([`/clients/details/${this.contact.id}`], {
			state: { returnTo: this.location.path() },
		});
	}

	sendSchedule(): void {
		console.log('SEND REQUEST');
	}

	openChat() {
		this.chatService.startConversation(
			{
				id: this.contact.refUserId,
				email: this.contact.email,
				photoUrl: this.contact.photoUrl,
				username: this.contact.displayName,
				role: this.chatService.role,
			},
			'contact-page'
		);
	}

	openModal() {
		this.atService.eventTracker(
			SCOPE_TYPE.Promptuary,
			SUBSCOPE_TYPE.Export,
			ITEM_TYPE.Page,
			INTERACTION_TYPE.View,
			{}
		);
		this.isExportModalOpen = true;
	}

	getTooltipLabel() {
		const firstName = this.contact.displayName.split(' ')[0];
		const periodTranslateKey = `CONTACT.RECURRENCY_PERIOD.${this.recurrencyData.recurrencyFrequency.toUpperCase()}`;
		const recurrencyDate = DateTime.fromISO(this.recurrencyData.recurrencyDate)
			.setLocale('pt-br')
			.toFormat('cccc, HH:mm');
		return `${firstName} ${this.translateService.instant(
			'CONTACT.RECURRENCY_TOOLTIP'
		)} ${this.translateService.instant(
			periodTranslateKey
		)}: <br> ${recurrencyDate}`;
	}
}
